/**
 * Seu email + código de ativação (proposta "Elements", 17/10/2026). Usado na ativação
 * (OnboardingPage, Máquina de Leads) e no "Já comprei? Liberar o acesso" da janela de compra.
 *
 * Valida no campo (email no formato, código com 5+ caracteres) em vez de não fazer nada. O
 * "Receber o código por email" manda o código para o email do primeiro campo (26/10/2026; antes
 * abria um painel com outro campo de email): GET /app/sendcode, com a resposta logo abaixo do
 * código. Sem email válido, aponta o erro no campo de email. O servidor não diz se o email tem
 * código, então a resposta é "Se houver um código…"; passou do limite de pedidos, diz quantos
 * minutos esperar (antes o painel dizia "Enviamos o código" em qualquer caso). O rótulo é "Seu email", e não "da
 * compra", porque quem chega de uma demonstração ainda não comprou. Quem envia o formulário é a
 * tela, que devolve o erro do servidor já mapeado para o campo.
 *
 * Movimento (23/10/2026): borda dos campos em 140 ms (.ds-campo; o foco não espera); o erro abre
 * espaço no lugar (.ds-altura) e troca o texto com fade; o código recusado pelo servidor treme uma
 * vez (.ds-tremida, M9 b), no máximo duas vezes seguidas, com o foco no campo e o código
 * selecionado; a resposta do envio do código abre espaço com .ds-altura; o botão espera o
 * servidor com o giro depois de 200 ms, com aria-disabled (continua focável; enviar de novo
 * enquanto espera não faz nada).
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { sendCode } from '../../api/useOnboarding';
import { getCookie, setCookie } from '../../lib/cookies';
import { AbreNoLugar, RotuloCarregando } from './Screen';
import { isEmail, useScreenText } from './screenText';

export interface FormError {
  field: 'email' | 'code' | 'form';
  text: string;
}

interface ActivationFormProps {
  submitLabel: string;
  /** Rótulo enquanto espera o servidor ("Liberando…"); entra com o giro depois de 200 ms. */
  busyLabel?: string;
  busy?: boolean;
  /** Erro do servidor, já com o campo. */
  error?: FormError | null;
  onSubmit: (email: string, code: string) => void;
  /** Some com o erro do servidor quando a pessoa mexe nos campos. */
  onEdit?: () => void;
  /** Foco no email ao abrir (a ativação chega aqui logo depois do aceite dos Termos). */
  autoFocus?: boolean;
}

type SendState = { kind: 'ok' | 'bad'; text: string } | null;

/** Do terceiro código recusado seguido em diante, o campo não treme mais (fica borda e mensagem). */
const TREMIDAS = 2;

export function ActivationForm({
  submitLabel,
  busyLabel,
  busy,
  error,
  onSubmit,
  onEdit,
  autoFocus,
}: ActivationFormProps): JSX.Element {
  const { tx } = useScreenText();
  const [email, setEmail] = useState(() => getCookie('email') ?? '');
  const [code, setCode] = useState(() => getCookie('purchase') ?? '');
  const [tried, setTried] = useState(false);
  // Pediu o código por email: o campo de email passa a ser conferido, como ao enviar.
  const [askedCode, setAskedCode] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendState, setSendState] = useState<SendState>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const codeRef = useRef<HTMLInputElement | null>(null);
  const recusas = useRef(0);

  const emailError =
    (tried || askedCode) && !isEmail(email)
      ? tx('err_email')
      : error?.field === 'email'
        ? error.text
        : '';
  const codeError =
    tried && code.trim().length < 5
      ? tx('err_code_short')
      : error?.field === 'code'
        ? error.text
        : '';

  // Código recusado pelo servidor (o erro do campo 'code' só vem da resposta): foco no campo com o
  // código selecionado e a tremida, que recomeça a cada recusa. Outro erro zera a sequência.
  useEffect(() => {
    if (!error) return;
    if (error.field !== 'code') {
      recusas.current = 0;
      return;
    }
    recusas.current += 1;
    const campo = codeRef.current;
    if (!campo) return;
    campo.focus();
    campo.select();
    if (recusas.current > TREMIDAS) return;
    campo.classList.remove('ds-tremida');
    void campo.offsetWidth; // recomeça a animação
    campo.classList.add('ds-tremida');
  }, [error]);

  function submit(e: FormEvent): void {
    e.preventDefault();
    setTried(true);
    if (!isEmail(email) || code.trim().length < 5 || busy) return;
    setCookie('email', email.trim());
    setCookie('purchase', code.trim());
    onSubmit(email.trim(), code.trim());
  }

  async function send(): Promise<void> {
    if (sending) return;
    const address = email.trim();
    setAskedCode(true);
    setSendState(null);
    if (!isEmail(address)) {
      emailRef.current?.focus();
      return;
    }
    setSending(true);
    try {
      const json = await sendCode(address);
      // A ordem importa: a homologação manda `found: true` em toda resposta, até no limite, e
      // `accepted` não diz se o email tem código (o servidor não revela). `found`/`expired` sozinhos
      // são a resposta da produção do Classic.
      if (json.temporarilyUnavailable) setSendState({ kind: 'bad', text: tx('err_generic') });
      else if (json.rateLimited)
        setSendState({
          kind: 'bad',
          text: tx('act_limited', { n: Math.max(1, Math.ceil((json.retryAfterSeconds ?? 60) / 60)) }),
        });
      else if (json.accepted === true || json.found === true) {
        const key = json.accepted === true ? 'act_sent_if' : 'act_sent';
        setSendState({ kind: 'ok', text: tx(key, { email: address }) });
        codeRef.current?.focus();
      } else if (json.expired) setSendState({ kind: 'bad', text: tx('act_expired') });
      else setSendState({ kind: 'bad', text: tx('act_not_found', { email: address }) });
    } catch {
      setSendState({ kind: 'bad', text: tx('err_generic') });
    } finally {
      setSending(false);
    }
  }

  return (
    <form className="as-form" onSubmit={submit} noValidate>
      <div className="as-field">
        <label htmlFor="email">{tx('act_email')}</label>
        <input
          ref={emailRef}
          id="email"
          className={`as-input ds-campo${emailError ? ' is-erro' : ''}`}
          type="email"
          autoComplete="email"
          autoFocus={autoFocus}
          value={email}
          aria-invalid={Boolean(emailError) || undefined}
          aria-describedby={emailError ? 'as-email-error' : undefined}
          onChange={(e) => {
            setEmail(e.target.value);
            setSendState(null);
            onEdit?.();
          }}
        />
        <FieldError id="as-email-error" text={emailError} />
      </div>

      <div className="as-field">
        <div className="as-field__label">
          <label htmlFor="purchase">{tx('act_code')}</label>
          <button
            type="button"
            className="as-link ds-resposta"
            aria-disabled={sending || undefined}
            onClick={() => void send()}
          >
            {sending ? tx('act_sending') : tx('act_receive')}
          </button>
        </div>
        <input
          ref={codeRef}
          id="purchase"
          className={`as-input as-input--code ds-campo${codeError ? ' is-erro' : ''}`}
          type="text"
          autoComplete="off"
          spellCheck={false}
          // Até 12 caracteres e sem o "HP…" de exemplo (27/10/2026): os códigos novos terão 5
          // dígitos. Os de 13 ou mais (411 de 13 no banco) não cabem, por decisão do Marcelo.
          maxLength={12}
          value={code}
          aria-invalid={Boolean(codeError) || undefined}
          aria-describedby={codeError ? 'as-code-error' : undefined}
          onChange={(e) => {
            setCode(e.target.value);
            onEdit?.();
          }}
          onAnimationEnd={(e) => e.currentTarget.classList.remove('ds-tremida')}
        />
        <FieldError id="as-code-error" text={codeError} />
        <AbreNoLugar aberto={Boolean(sendState)}>
          {sendState ? (
            <p
              className={`as-sent${sendState.kind === 'bad' ? ' as-sent--bad' : ''}`}
              role="status"
            >
              <span className="material-symbols-rounded" aria-hidden="true">
                {sendState.kind === 'ok' ? 'check_circle' : 'error'}
              </span>
              {sendState.text}
            </p>
          ) : null}
        </AbreNoLugar>
      </div>

      <FieldError text={error?.field === 'form' ? error.text : ''} />

      <button
        type="submit"
        id="button_send"
        className="as-btn as-btn--primary as-btn--block ds-resposta"
        aria-disabled={busy || undefined}
        aria-busy={busy || undefined}
      >
        <RotuloCarregando ocupado={Boolean(busy)} rotulo={submitLabel} rotuloOcupado={busyLabel} />
      </button>
    </form>
  );
}

/**
 * Erro no lugar (movimento, 23/10/2026): com texto, abre espaço (.ds-altura) e entra; sem texto,
 * sai e o espaço fecha. Texto novo com o erro aberto troca no lugar, com fade.
 */
export function FieldError({ text, id }: { text: string; id?: string }): JSX.Element | null {
  const ultimo = useRef(text);
  if (text) ultimo.current = text;
  const mostrado = text || ultimo.current;
  return (
    <AbreNoLugar aberto={Boolean(text)}>
      <p className="as-error" id={id} role="alert">
        <span className="material-symbols-rounded" aria-hidden="true">
          error
        </span>
        <span key={mostrado} className="ds-aparece">
          {mostrado}
        </span>
      </p>
    </AbreNoLugar>
  );
}
